// AvatarPreferenceModal.jsx
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

// TODO: Re-enable AI avatar generation once the generation service is restored.
// Tracked in: Phase E — restore /api/avatar generate-from-preferences & generate-from-photo

function getInitials(name) {
  if (!name || typeof name !== 'string') return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

const AvatarPreferenceModal = ({ isOpen, onClose, displayName = 'User' }) => {
  const dialogRef = useRef(null);
  const initials = getInitials(displayName);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => dialogRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={onClose}
      >
        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="avatar-modal-title"
          tabIndex={-1}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          className="surface-panel p-8 max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 id="avatar-modal-title" className="font-display text-h2 text-primary">
              Avatar
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost p-2"
              aria-label="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col items-center text-center">
            <div
              className="w-24 h-24 rounded-full bg-pine flex items-center justify-center border border-hairline mb-4"
              aria-hidden="true"
            >
              <span className="font-display text-h1 text-bone-raised">{initials}</span>
            </div>
            <p className="text-label text-ink-muted max-w-sm">
              AI Avatar generation is temporarily unavailable. Your initials avatar will be used
              for now.
            </p>
          </div>

          <div className="mt-8 flex justify-end">
            <button type="button" onClick={onClose} className="btn-primary px-6 py-2 text-label">
              Done
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AvatarPreferenceModal;
